import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useLocation } from "react-router-dom";
import TranslationDashboard from "./pages/Dashboard";
import AppSuccessDashboard from "./pages/AppSuccessDashboard";
import Login from "./pages/Login";
import RepositorySelection from "./pages/RepositorySelection";
import TranslationJob from "./pages/TranslationJob";
import AppResearch from "./pages/AppResearch";
import AppRegistration from "./pages/AppRegistration";
import Legal from "./pages/Legal";
import DeploymentChecklist from "./pages/DeploymentChecklist";
import AppIconGenerator from "./pages/AppIconGenerator";
import AppSplashScreenGenerator from "./pages/AppSplashScreenGenerator";
import AppIdeaResearch from "./pages/AppIdeaResearch";
import ScreenshotCreator from "./pages/ScreenshotCreator";
import UGCVideoCreator from "./pages/UGCVideoCreator";
import SlideshowMaker from "./pages/SlideshowMaker";
import MascotCreator from "./pages/MascotCreator";
import DevelopmentFramework from "./pages/DevelopmentFramework";
import AppEngagement from "./pages/AppEngagement";
import ImplementationExpander from "./pages/ImplementationExpander";
import AppMarketing from "./pages/AppMarketing";
import AppStoreListing from "./pages/AppStoreListing";
import GooglePlayReleaseNotes from "./pages/GooglePlayReleaseNotes";
import Subscriptions from "./pages/Subscriptions";
import AppStoreViolationChecker from "./pages/AppStoreViolationChecker";
import ReviewAnalysis from "./pages/ReviewAnalysis";
import ProgrammaticSEO from "./pages/ProgrammaticSEO";
import CrossPromotion from "./pages/CrossPromotion";
import AdvancedLab from "./pages/AdvancedLab";
import AppGrowth from "./pages/AppGrowth";
import AppPerformance from "./pages/AppPerformance";
import AppFeatureGraphicGenerator from "./pages/AppFeatureGraphicGenerator";
import AppleCreativeAssets from "./pages/AppleCreativeAssets";
import PublishReadyDashboard from "./pages/PublishReadyDashboard";
import Settings from "./pages/Settings";
import PublicLegalDocument from "./pages/PublicLegalDocument";
import SmartLinks from "./pages/SmartLinks";
import PublicSmartLink from "./pages/PublicSmartLink";
import SideNav from "./components/SideNav";
import NotFound from "./pages/NotFound";
import { useAuth } from "./hooks/useAuth";
import { useUISettings } from "./hooks/useUISettings";

const App: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { autoHideNavigation } = useUISettings();
  const [sideNavCollapsed, setSideNavCollapsed] = React.useState(false);
  const [sideNavHoverExpanded, setSideNavHoverExpanded] = React.useState(false);
  const hoverOpenTimeoutRef = React.useRef<number | null>(null);
  const hoverCloseTimeoutRef = React.useRef<number | null>(null);

  const navCollapsed = autoHideNavigation
    ? !sideNavHoverExpanded
    : sideNavCollapsed;
  const isPublicLegalRoute =
    location.pathname === "/privacy" ||
    location.pathname === "/terms" ||
    location.pathname === "/eula" ||
    location.pathname === "/cookie" ||
    location.pathname === "/dpa";
  const isPublicSmartLinkRoute = location.pathname.startsWith("/go/");

  const clearHoverTimers = React.useCallback(() => {
    if (hoverOpenTimeoutRef.current) {
      window.clearTimeout(hoverOpenTimeoutRef.current);
      hoverOpenTimeoutRef.current = null;
    }
    if (hoverCloseTimeoutRef.current) {
      window.clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (autoHideNavigation) {
      clearHoverTimers();
      setSideNavHoverExpanded(false);
    }
  }, [autoHideNavigation, clearHoverTimers]);

  React.useEffect(() => {
    return () => {
      clearHoverTimers();
    };
  }, [clearHoverTimers]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {user && !isPublicSmartLinkRoute && (
        <SideNav
          collapsed={navCollapsed}
          autoHideEnabled={autoHideNavigation}
          onToggleCollapse={() => {
            if (autoHideNavigation) return;
            setSideNavCollapsed((currentState) => !currentState);
          }}
          onHoverExpandChange={(expanded) => {
            if (!autoHideNavigation) return;
            if (expanded) {
              if (hoverCloseTimeoutRef.current) {
                window.clearTimeout(hoverCloseTimeoutRef.current);
                hoverCloseTimeoutRef.current = null;
              }
              if (sideNavHoverExpanded) return;
              if (hoverOpenTimeoutRef.current) {
                window.clearTimeout(hoverOpenTimeoutRef.current);
              }
              hoverOpenTimeoutRef.current = window.setTimeout(() => {
                setSideNavHoverExpanded(true);
                hoverOpenTimeoutRef.current = null;
              }, 140);
              return;
            }

            if (hoverOpenTimeoutRef.current) {
              window.clearTimeout(hoverOpenTimeoutRef.current);
              hoverOpenTimeoutRef.current = null;
            }
            if (!sideNavHoverExpanded) return;
            if (hoverCloseTimeoutRef.current) {
              window.clearTimeout(hoverCloseTimeoutRef.current);
            }
            hoverCloseTimeoutRef.current = window.setTimeout(() => {
              setSideNavHoverExpanded(false);
              hoverCloseTimeoutRef.current = null;
            }, 220);
          }}
        />
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: 1,
          minWidth: 0,
          marginLeft: user ? 0 : 0,
        }}
      >
        <Box
          component="main"
          sx={{
            mt: isPublicSmartLinkRoute ? 0 : 3,
            mb: isPublicSmartLinkRoute ? 0 : 3,
            flex: 1,
            width: 1,
            maxWidth: "none",
            pl: isPublicSmartLinkRoute ? 0 : user ? (navCollapsed ? 2 : 3) : 2,
            pr: isPublicSmartLinkRoute ? 0 : 2,
            transition: "padding-left 260ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <Routes>
            <Route
              path="/privacy"
              element={<PublicLegalDocument type="privacy" />}
            />
            <Route path="/terms" element={<PublicLegalDocument type="tos" />} />
            <Route path="/eula" element={<PublicLegalDocument type="eula" />} />
            <Route
              path="/cookie"
              element={<PublicLegalDocument type="cookie" />}
            />
            <Route path="/dpa" element={<PublicLegalDocument type="dpa" />} />
            <Route path="/go/:slug" element={<PublicSmartLink />} />
            <Route
              path="/login"
              element={!user ? <Login /> : <Navigate to="/" />}
            />
            <Route
              path="/"
              element={
                user ? <AppSuccessDashboard /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/translation"
              element={
                user ? <TranslationDashboard /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/repositories"
              element={
                user ? <RepositorySelection /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/job/:jobId"
              element={user ? <TranslationJob /> : <Navigate to="/login" />}
            />
            <Route
              path="/app-research"
              element={user ? <AppResearch /> : <Navigate to="/login" />}
            />
            <Route
              path="/app-registration"
              element={user ? <AppRegistration /> : <Navigate to="/login" />}
            />
            <Route
              path="/legal"
              element={user ? <Legal /> : <Navigate to="/login" />}
            />
            <Route
              path="/deployment-checklist"
              element={
                user ? <DeploymentChecklist /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/icon-generator"
              element={user ? <AppIconGenerator /> : <Navigate to="/login" />}
            />
            <Route
              path="/splash-screen-generator"
              element={
                user ? <AppSplashScreenGenerator /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/feature-graphic-generator"
              element={
                user ? <AppFeatureGraphicGenerator /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/apple-creative-assets"
              element={user ? <AppleCreativeAssets /> : <Navigate to="/login" />}
            />
            <Route
              path="/app-idea-research"
              element={user ? <AppIdeaResearch /> : <Navigate to="/login" />}
            />
            <Route
              path="/screenshot-creator"
              element={user ? <ScreenshotCreator /> : <Navigate to="/login" />}
            />
            <Route
              path="/ugc-video-creator"
              element={user ? <UGCVideoCreator /> : <Navigate to="/login" />}
            />
            <Route
              path="/slideshow-maker"
              element={user ? <SlideshowMaker /> : <Navigate to="/login" />}
            />
            <Route
              path="/mascot-creator"
              element={user ? <MascotCreator /> : <Navigate to="/login" />}
            />
            <Route
              path="/development-framework"
              element={
                user ? <DevelopmentFramework /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/app-engagement"
              element={user ? <AppEngagement /> : <Navigate to="/login" />}
            />
            <Route
              path="/app-performance"
              element={user ? <AppPerformance /> : <Navigate to="/login" />}
            />
            <Route
              path="/app-growth"
              element={user ? <AppGrowth /> : <Navigate to="/login" />}
            />
            <Route
              path="/implementation-plan-generator"
              element={
                user ? <ImplementationExpander /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/implementation-expander"
              element={<Navigate to="/implementation-plan-generator" replace />}
            />
            <Route
              path="/app-marketing"
              element={user ? <AppMarketing /> : <Navigate to="/login" />}
            />
            <Route
              path="/app-store-listing"
              element={user ? <AppStoreListing /> : <Navigate to="/login" />}
            />
            <Route
              path="/google-play-release-notes"
              element={
                user ? <GooglePlayReleaseNotes /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/subscriptions"
              element={user ? <Subscriptions /> : <Navigate to="/login" />}
            />
            <Route
              path="/app-store-violation-checker"
              element={
                user ? <AppStoreViolationChecker /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/review-analysis"
              element={user ? <ReviewAnalysis /> : <Navigate to="/login" />}
            />
            <Route
              path="/publish-ready"
              element={
                user ? <PublishReadyDashboard /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/settings"
              element={user ? <Settings /> : <Navigate to="/login" />}
            />
            <Route
              path="/programmatic-seo"
              element={user ? <ProgrammaticSEO /> : <Navigate to="/login" />}
            />
            <Route
              path="/cross-promotion"
              element={user ? <CrossPromotion /> : <Navigate to="/login" />}
            />
            <Route
              path="/smart-links"
              element={user ? <SmartLinks /> : <Navigate to="/login" />}
            />
            <Route
              path="/advanced"
              element={user ? <AdvancedLab /> : <Navigate to="/login" />}
            />
            <Route
              path="/advanced/:featureId"
              element={user ? <AdvancedLab /> : <Navigate to="/login" />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>
        {!isPublicLegalRoute && !isPublicSmartLinkRoute && (
          <Box
            component="footer"
            sx={{
              py: 3,
              px: 3,
              mt: "auto",
              backgroundColor: "background.paper",
              borderTop: "1px solid",
              borderColor: "divider",
              width: 1,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              &copy; {new Date().getFullYear()} App Success Platform
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default App;
